import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature, MarketplacePaymentClient } from 'src/WebApiController';

/**
 * Resolves the Adyen back-office (Customer Area) deeplink for a payment's PSP reference (POS-9098).
 * Returns null when there is no reference or the backend cannot build a URL. Gated behind the
 * RetailSale feature flag. The caller should only pass a real Adyen PSP reference (not the
 * merchant-ref fallback and not an external payment reference).
 */
export function useAdyenPaymentDeeplink(
  referenceId?: string | null,
  disabled?: boolean
) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const shouldQuery =
    hasRetailSaleFeature &&
    !!activeAccountWebClientConfig?.activeAccountId &&
    !!referenceId &&
    !disabled;

  return useQuery({
    queryKey: ['getAdyenPaymentDeeplinkByReferenceId', referenceId],
    async queryFn() {
      if (!shouldQuery) return null;

      try {
        return await new MarketplacePaymentClient(
          activeAccountWebClientConfig
        ).getAdyenPaymentDeeplinkByReferenceId(referenceId!);
      } catch (err: unknown) {
        trackError(
          'MarketplacePaymentClient.getAdyenPaymentDeeplinkByReferenceId',
          err as ErrorTypes,
          { referenceId }
        );
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });
}
